import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import RankWidget from '../components/RankWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Target, Calendar, BarChart3,
  Activity, ArrowUpRight, ArrowDownRight, Eye, History,
  LineChart, CalendarDays, Briefcase, Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManagerDashboard = ({ user, onLogout }) => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentHistory, setAgentHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('overview');

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const now = new Date();
      let month = now.getMonth() + 1;
      let year = now.getFullYear();
      
      if (selectedMonth === 'previous') {
        if (month === 1) {
          month = 12;
          year = year - 1;
        } else {
          month = month - 1;
        }
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/performance/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year }
      });
      setPerformanceData(response.data);
    } catch (error) {
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  const fetchAgentHistory = async (email) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/performance/agent/${email}/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { months_back: 12 }
      });
      setAgentHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch agent history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setActiveHistoryTab('overview');
    fetchAgentHistory(agent.email);
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-emerald-600';
    if (growth < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  const getGrowthBg = (growth) => {
    if (growth > 0) return 'bg-emerald-50';
    if (growth < 0) return 'bg-red-50';
    return 'bg-slate-50';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUpRight className="w-4 h-4" />;
    if (growth < 0) return <ArrowDownRight className="w-4 h-4" />;
    return null;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-5 h-5 text-emerald-600" />;
    if (trend === 'declining') return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <Activity className="w-5 h-5 text-slate-500" />;
  };

  // Prepare daily sales chart data
  const prepareDailyChartData = () => {
    if (!performanceData?.daily_sales) return [];
    return performanceData.daily_sales.map(item => ({
      date: item.date_short || item.date,
      sales: item.count,
      fullDate: item.date
    }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400">Loading dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  const data = performanceData;
  const dailyChartData = prepareDailyChartData();

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-6 overflow-auto" data-testid="manager-dashboard">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">Team Dashboard</h1>
                <p className="text-slate-400 text-sm">Monitor team performance</p>
              </div>
            </div>
            
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-slate-200 rounded-lg h-10 hover:bg-slate-700/50 transition-colors">
                <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="current">{data?.month_name} {data?.year}</SelectItem>
                <SelectItem value="previous">{data?.prev_month_name}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Team Overview Card - Manager Stats */}
          <Card className="bg-gradient-to-r from-amber-600 to-amber-500 border-0 shadow-lg shadow-amber-500/20">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-amber-100 text-sm font-medium mb-1">
                    <Users className="w-4 h-4" />
                    TEAM OVERVIEW
                  </div>
                  <div className="flex items-end gap-3">
                    <span className="text-4xl font-bold text-white">{data?.summary?.total_sales?.toLocaleString() || 0}</span>
                    <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-medium ${
                      data?.summary?.growth > 0 ? 'bg-emerald-500/30 text-emerald-100' : 
                      data?.summary?.growth < 0 ? 'bg-red-500/30 text-red-100' : 
                      'bg-white/20 text-white'
                    }`}>
                      {getGrowthIcon(data?.summary?.growth)}
                      {data?.summary?.growth > 0 ? '+' : ''}{data?.summary?.growth || 0}%
                    </span>
                  </div>
                  <p className="text-amber-100 text-sm mt-1">Total team sales this month</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center px-4 py-2 bg-white/15 rounded-lg backdrop-blur-sm">
                    <p className="text-2xl font-bold text-white">{data?.agent_stats?.[0]?.name?.split(' ')[0] || '-'}</p>
                    <p className="text-amber-100 text-xs mt-1">Top Performer</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/15 rounded-lg backdrop-blur-sm">
                    <p className="text-2xl font-bold text-white">{data?.summary?.daily_average || 0}</p>
                    <p className="text-amber-100 text-xs mt-1">Avg/Day</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/15 rounded-lg backdrop-blur-sm">
                    <p className="text-2xl font-bold text-white">{data?.summary?.total_agents || 0}</p>
                    <p className="text-amber-100 text-xs mt-1">Active Agents</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Team Sales</p>
                    <p className="text-2xl font-bold text-white mt-1">{data?.summary?.total_sales?.toLocaleString()}</p>
                    <div className={`flex items-center gap-1 mt-1 ${getGrowthColor(data?.summary?.growth)}`}>
                      {getGrowthIcon(data?.summary?.growth)}
                      <span className="text-xs font-medium">
                        {data?.summary?.growth > 0 ? '+' : ''}{data?.summary?.growth}%
                      </span>
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Target className="w-4 h-4 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Daily Average</p>
                    <p className="text-2xl font-bold text-white mt-1">{data?.summary?.daily_average}</p>
                    <p className="text-xs text-slate-500 mt-1">{data?.days_for_average || data?.days_counted} days (excl. today)</p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Team Size</p>
                    <p className="text-2xl font-bold text-white mt-1">{data?.summary?.total_agents}</p>
                    <p className="text-xs text-slate-500 mt-1">Active agents</p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Last Month</p>
                    <p className="text-2xl font-bold text-white mt-1">{data?.summary?.prev_month_sales?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">{data?.prev_month_name}</p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <History className="w-4 h-4 text-violet-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Sales Chart */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                Daily Sales Trend
                <span className="text-sm font-normal text-slate-400 ml-2">— {data?.month_name} {data?.year}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesGradientManager" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      dy={10}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                        padding: '8px 12px'
                      }}
                      labelStyle={{ color: '#f8fafc', fontWeight: '600', marginBottom: '4px' }}
                      itemStyle={{ color: '#f59e0b' }}
                      formatter={(value) => [`${value} sales`, 'Sales']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      fill="url(#salesGradientManager)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Team Table */}
            <Card className="lg:col-span-2 bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardHeader className="pb-3 border-b border-slate-700/50">
                <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  Team Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700/50 bg-slate-800/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Agent</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Sales</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Growth</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {data?.agent_stats?.map((agent) => (
                        <tr 
                          key={agent.email} 
                          className={`hover:bg-slate-700/30 transition-colors group ${
                            agent.email === user?.email ? 'bg-amber-500/10' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-semibold ${
                              agent.rank === 1 ? 'bg-amber-500/20 text-amber-400' :
                              agent.rank === 2 ? 'bg-slate-600/50 text-slate-300' :
                              agent.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                              'bg-slate-700/50 text-slate-400'
                            }`}>
                              {agent.rank}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium text-white">{agent.name}</p>
                                {agent.email === user?.email && (
                                  <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400 mt-0.5">You</Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-lg font-semibold text-white">{agent.current_month_sales}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getGrowthBg(agent.growth)} ${getGrowthColor(agent.growth)}`}>
                              {getGrowthIcon(agent.growth)}
                              {agent.growth > 0 ? '+' : ''}{agent.growth}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAgentClick(agent)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              History
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Offer Stats */}
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardHeader className="pb-3 border-b border-slate-700/50">
                <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-500" />
                  Top Offers
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {data?.offer_stats?.slice(0, 6).map((offer, idx) => {
                    const maxCount = data?.offer_stats?.[0]?.current_count || 1;
                    const percentage = (offer.current_count / maxCount) * 100;
                    
                    return (
                      <div key={offer.offer}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-slate-300 font-medium truncate max-w-[140px]">{offer.offer}</span>
                          <span className="text-sm font-semibold text-white">{offer.current_count}</span>
                        </div>
                        <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </main>

      {/* Agent History Modal */}
      <Dialog open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader className="pb-4 border-b border-slate-700">
            <DialogTitle className="text-lg font-semibold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <span>{selectedAgent?.name}</span>
                <p className="text-sm font-normal text-slate-400">Performance History</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : agentHistory && (
            <div className="space-y-6 pt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase">Total Sales (12M)</p>
                  <p className="text-2xl font-bold text-white mt-1">{agentHistory.summary?.total_sales?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase">Daily Average</p>
                  <p className="text-2xl font-bold text-amber-500 mt-1">{agentHistory.summary?.overall_daily_average}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase">Best Month</p>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{agentHistory.summary?.best_month?.sales}</p>
                  <p className="text-xs text-slate-500">{agentHistory.summary?.best_month?.month_name}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase">Trend</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getTrendIcon(agentHistory.summary?.trend)}
                    <span className="text-lg font-semibold text-white capitalize">{agentHistory.summary?.trend}</span>
                  </div>
                </div>
              </div>

              {/* Monthly Sales Chart */}
              <div className="bg-slate-700/30 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <LineChart className="w-4 h-4 text-amber-500" />
                  Monthly Performance
                </h4>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agentHistory.history?.slice(0, 12).reverse()} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                      <XAxis 
                        dataKey="month_short" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                        labelStyle={{ color: '#f8fafc' }}
                        itemStyle={{ color: '#f59e0b' }}
                        formatter={(value) => [`${value} sales`, 'Sales']}
                        labelFormatter={(label, payload) => `${payload?.[0]?.payload?.month_name} ${payload?.[0]?.payload?.year}`}
                      />
                      <Bar dataKey="sales" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerDashboard;
